/**
 * The geometry under the Traffic card's trace — pure, DOM-free, so the part
 * that can be got subtly wrong (a curve that dips below the zero line, a
 * tween that pairs the wrong points) is testable in node without the card
 * around it.
 *
 * Two jobs:
 *  · `trafficCurve` turns a series of points into SVG path data, and the same
 *    curve closed down to the baseline for the soft fill. Two styles:
 *      `monotone` (the default) — Fritsch–Carlson tangents: never leaves the
 *      band between two neighbouring samples, so an idle link never dips
 *      under the zero line, a burst is as tall as the chassis said and no
 *      taller, and equal readings draw dead flat. Rendered side by side on
 *      2026-09-18 ("curvy smoother curves not sharp points", the operator's
 *      words), the sharp points turned out to be the WINDOW, not the curve: a
 *      one-sample burst on a ~1.3 s cadence is five pixels wide on a
 *      three-minute plot whatever the spline. On a sixty-second live window
 *      the same burst is a rounded hill, and the monotone curve draws a
 *      multi-sample hump as one clean mountain.
 *      `bump` — a horizontal tangent at every knot (each rise an S over its
 *      span). Rounds a lone tip a touch more, but stair-steps a hump that
 *      spans several samples, which is why it is not the default.
 *      `cardinal` — Catmull-Rom tangents held inside the plot; its pre-swing
 *      before a spike goes the wrong way, so held at the baseline it only
 *      rounds the tip.
 *  · `tweenSeries` gives the frame between two shapes of the trace, so a fresh
 *    sample eases into place over `EASE_MS` instead of snapping — the jerk the
 *    operator called out on the live meter.
 *
 * Points are `{x, y}` in whatever space the caller likes — pixels for the path
 * builder, time × rate for the tween — and a `null` is a reading the chassis
 * did not give: the pen lifts there and comes back down at the next real one.
 */

export type CurvePoint = { x: number; y: number };
/** A null is a gap; a point with a non-finite coordinate is treated as one too. */
export type CurveSeries = ReadonlyArray<CurvePoint | null>;

/** How long a landed sample takes to ease into the trace. */
export const EASE_MS = 450;

/** Two decimals is a tenth of a device pixel on a retina screen — enough, and it keeps the path short. */
function fmt(v: number): string {
  return String(Math.round(v * 100) / 100);
}

function usable(p: CurvePoint | null | undefined): p is CurvePoint {
  return p != null && Number.isFinite(p.x) && Number.isFinite(p.y);
}

/** The pen-down stretches of a series: consecutive usable points, split wherever a gap sits. */
export function runsOf(points: CurveSeries): CurvePoint[][] {
  const runs: CurvePoint[][] = [];
  let run: CurvePoint[] = [];
  for (const p of points) {
    if (usable(p)) {
      run.push(p);
      continue;
    }
    if (run.length) runs.push(run);
    run = [];
  }
  if (run.length) runs.push(run);
  return runs;
}

/** The last real reading in a series — where the end-dot sits — or null when there is none. */
export function newestPoint(points: CurveSeries): CurvePoint | null {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const p = points[i];
    if (usable(p)) return p;
  }
  return null;
}

/**
 * Fritsch–Carlson tangents for one run of points with non-decreasing x.
 *
 * Secants first; an interior tangent is the mean of its two secants unless
 * they disagree in sign (a local peak or trough), where it is zero so the
 * curve turns flat at the extreme instead of overshooting it. Then the
 * classic clamp: where a tangent would exceed three times the secant it is
 * pulled back onto the circle of radius three, which is the condition that
 * keeps every segment monotone. A flat secant zeroes both its tangents, so
 * equal readings draw a straight line. Two points at the same x (h = 0) get
 * a zero secant rather than a division by nought — the segment degenerates to
 * a vertical join and nothing becomes NaN.
 */
export function monotoneTangents(run: readonly CurvePoint[]): number[] {
  const n = run.length;
  if (n < 2) return new Array<number>(n).fill(0);
  const delta: number[] = new Array(n - 1);
  for (let k = 0; k < n - 1; k += 1) {
    const a = run[k]!;
    const b = run[k + 1]!;
    const h = b.x - a.x;
    delta[k] = h > 0 ? (b.y - a.y) / h : 0;
  }
  const m: number[] = new Array(n);
  m[0] = delta[0]!;
  m[n - 1] = delta[n - 2]!;
  for (let k = 1; k < n - 1; k += 1) {
    const d0 = delta[k - 1]!;
    const d1 = delta[k]!;
    m[k] = d0 * d1 <= 0 ? 0 : (d0 + d1) / 2;
  }
  for (let k = 0; k < n - 1; k += 1) {
    const d = delta[k]!;
    if (d === 0) {
      m[k] = 0;
      m[k + 1] = 0;
      continue;
    }
    const alpha = m[k]! / d;
    const beta = m[k + 1]! / d;
    const s = alpha * alpha + beta * beta;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      m[k] = tau * alpha * d;
      m[k + 1] = tau * beta * d;
    }
  }
  return m;
}

/**
 * Catmull-Rom tangents for one run: at each interior knot the slope of the
 * chord between its neighbours, scaled by (1 − tension); a one-sided secant at
 * either end. Tension 0 is the classic Catmull-Rom, 1 is straight segments.
 * Two knots at the same x get a zero tangent rather than a division by nought.
 */
export function cardinalTangents(run: readonly CurvePoint[], tension = 0.35): number[] {
  const n = run.length;
  const m: number[] = new Array<number>(n).fill(0);
  if (n < 2) return m;
  const k = 1 - tension;
  const slope = (a: CurvePoint, b: CurvePoint) => (b.x - a.x > 0 ? (b.y - a.y) / (b.x - a.x) : 0);
  m[0] = k * slope(run[0]!, run[1]!);
  m[n - 1] = k * slope(run[n - 2]!, run[n - 1]!);
  for (let i = 1; i < n - 1; i += 1) m[i] = k * slope(run[i - 1]!, run[i + 1]!);
  return m;
}

export type CurveStyle = 'monotone' | 'bump' | 'cardinal';

export interface CurveOptions {
  style?: CurveStyle;
  /**
   * The plot's top in the same y space as the points (pixel space: the
   * smallest y drawn). A cardinal shoulder is held at it, as the baseline
   * holds the curve's bottom; absent, only the baseline guards.
   */
  topY?: number;
}

/**
 * One run as cubic Béziers: a Hermite segment with tangents m0/m1 over a step
 * h is the Bézier whose control points sit a third of the way along at slope
 * m. The bump style's tangents are all zero, so its control ys are the endpoint
 * ys and the segment is an S that stays inside the band. For the cardinal
 * style the control ys are held inside the plot — between `topY` and `baseY`
 * — which stops that spline dipping under the zero line or out of the chart.
 */
function runPath(run: readonly CurvePoint[], baseY: number, opts: CurveOptions): string {
  const style = opts.style ?? 'monotone';
  const cardinal = style === 'cardinal';
  const m =
    style === 'bump'
      ? new Array<number>(run.length).fill(0)
      : cardinal
        ? cardinalTangents(run)
        : monotoneTangents(run);
  const lo = opts.topY ?? Number.NEGATIVE_INFINITY;
  const hold = (y: number) => (cardinal ? Math.min(baseY, Math.max(lo, y)) : y);
  let d = `M ${fmt(run[0]!.x)} ${fmt(run[0]!.y)}`;
  for (let k = 0; k < run.length - 1; k += 1) {
    const a = run[k]!;
    const b = run[k + 1]!;
    const h = (b.x - a.x) / 3;
    d += ` C ${fmt(a.x + h)} ${fmt(hold(a.y + m[k]! * h))} ${fmt(b.x - h)} ${fmt(hold(b.y - m[k + 1]! * h))} ${fmt(b.x)} ${fmt(b.y)}`;
  }
  return d;
}

/**
 * The stroke and the fill for one series.
 *
 * `line` is one `M … C …` subpath per run; `area` is the same curve dropped
 * to `baseY` at each end and closed, one closed subpath per run, so the fill
 * under a broken trace has the same holes as the stroke above it. A run of a
 * single point yields no segment in either — there is nothing to draw between
 * one reading and itself; the card's end-dot is what marks a lone point.
 */
export function trafficCurve(
  points: CurveSeries,
  baseY: number,
  opts: CurveOptions = {},
): { line: string; area: string } {
  const lines: string[] = [];
  const areas: string[] = [];
  for (const run of runsOf(points)) {
    if (run.length < 2) continue;
    const d = runPath(run, baseY, opts);
    lines.push(d);
    const first = run[0]!;
    const last = run[run.length - 1]!;
    areas.push(`${d} L ${fmt(last.x)} ${fmt(baseY)} L ${fmt(first.x)} ${fmt(baseY)} Z`);
  }
  return { line: lines.join(' '), area: areas.join(' ') };
}

/** Cubic ease-out, clamped: fast off the mark, settling gently — the feel of a needle, not a step. */
export function easeOut(t: number): number {
  const c = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const u = 1 - c;
  return 1 - u * u * u;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * The frame at progress `t` (0 = the old shape, 1 = the new) between two
 * shapes of one series, paired by x rather than by index: a live window drops
 * a point off its head as often as it gains one at its tail, and pairing by
 * index would slide every y one step along and draw a wave that was never
 * measured. A point present in both eases from its old y; the newest point,
 * new to the series, rises from the old newest y — a sample lands as the line
 * extending, not as a dot appearing; anything else new is simply drawn where
 * it is. Gaps stay gaps.
 */
export function tweenSeries(
  from: CurveSeries,
  to: CurveSeries,
  t: number,
): Array<CurvePoint | null> {
  if (t >= 1 || from.length === 0) return to.map((p) => (usable(p) ? { x: p.x, y: p.y } : null));
  const oldY = new Map<number, number>();
  for (const p of from) if (usable(p)) oldY.set(p.x, p.y);
  const oldNewest = newestPoint(from);
  const newest = newestPoint(to);
  return to.map((p) => {
    if (!usable(p)) return null;
    const prev = oldY.get(p.x);
    if (prev !== undefined) return { x: p.x, y: lerp(prev, p.y, t) };
    if (oldNewest && newest && p === newest) return { x: p.x, y: lerp(oldNewest.y, p.y, t) };
    return { x: p.x, y: p.y };
  });
}
